import React, { useState, useEffect } from 'react';
//@ts-ignore
import * as styles from './blog-scroll.module.css';
//@ts-ignore
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import ArrowLgButton from '../Global/ArrowLgButton';
import {
    getMediaData,
    getMultiChoice,
    getTimeToRead,
    optimizeKontentImageUrl,
    buildKontentSrcSet,
} from '../Global/DataUtils';
import { BlogPagePreview } from '../Global/DataUtils/blogQueries';
import LazyImage from '../Global/LazyImage';
import { BlogScrollProps } from './index';
import { getImageData } from '../Global/DataUtils/gatsbyImageUtils';
import { getLangPrefix } from '../../global/countries';
import CtaLink from '../Global/CtaLink';
import { useIsClient } from '../Global/DataUtils/useIsClient';

interface Props extends BlogScrollProps {
    content: BlogPagePreview[];
    preferredLanguage?: string;
}

const BlogScroll: React.FC<Props> = ({
    kontentItemId,
    anchor_name,
    cta_label,
    headline,
    relative_large_display,
    relative_small_display,
    ...props
}) => {
    if (props.content.length === 0) return null;

    const breakpoints = useBreakpoint();
    const isClient = useIsClient();
    const isLargeScreen = isClient && breakpoints.l;
    const contentDisplayType = getMultiChoice(props.content_display_type);
    const layoutDisplayType = getMultiChoice(props.layout_display_type);
    const isGalleryLayout = layoutDisplayType === 'gallery';
    const langPrefix = getLangPrefix(props.preferredLanguage);

    const sliceContent = () => {
        const relativeLargeDisplay = relative_large_display?.value || 6;
        const relativeSmallDisplay = relative_small_display?.value || 4;

        if (breakpoints.l) {
            return props.content.slice(0, relativeLargeDisplay);
        }

        return props.content.slice(0, relativeSmallDisplay);
    };

    let content =
        contentDisplayType === 'relative' ? sliceContent() : props.content;

    const currentDisplayNumber = breakpoints.l ? 3 : 1;
    const [visibleItems, setVisibleItems] = useState(
        content?.slice(0, currentDisplayNumber)
    );
    const [currentItem, setCurrentItem] = useState(0);

    useEffect(() => {
        setVisibleItems(content?.slice(0, currentDisplayNumber));
    }, [currentDisplayNumber]);

    useEffect(() => {
        content =
            contentDisplayType === 'relative' ? sliceContent() : props.content;
    }, [content]);

    const handleClickPrev = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        if (typeof window !== 'undefined' && window.innerWidth > 991) {
            let prevItems: BlogPagePreview[] = [];

            if (currentItem > 1) {
                prevItems = content.slice(currentItem - 2, currentItem + 1);
                prevItems = [prevItems[1], prevItems[0], prevItems[2]];
            } else if (currentItem === 1) {
                prevItems = content.slice(0, currentDisplayNumber);
                prevItems = [prevItems[0], prevItems[1], prevItems[2]];
            } else {
                prevItems = content.slice(0, currentDisplayNumber);
                prevItems = [prevItems[1], prevItems[0], prevItems[2]];
            }

            setVisibleItems(prevItems);

            if (currentItem > 0) {
                setCurrentItem(currentItem - 1);
            }
        } else {
            const newItem = currentItem - 1;
            setVisibleItems(
                content?.slice(newItem, newItem + currentDisplayNumber)
            );
            setCurrentItem(newItem);
        }
    };

    const handleClickNext = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        if (typeof window !== 'undefined' && window.innerWidth > 991) {
            let nextItems = content?.slice(
                currentItem,
                currentItem + currentDisplayNumber
            );
            if (currentItem > 0) {
                nextItems = [nextItems[1], nextItems[0], nextItems[2]];
            } else if (currentItem == 0) {
                nextItems = content?.slice(0, currentDisplayNumber);
                nextItems = [nextItems[1], nextItems[0], nextItems[2]];
            }
            setVisibleItems(nextItems);

            setCurrentItem(currentItem + 1);
        } else {
            const newItem = currentItem + 1;
            setVisibleItems(
                content?.slice(newItem, newItem + currentDisplayNumber)
            );
            setCurrentItem(newItem);
        }
    };

    const renderItem = (scrollItem: BlogPagePreview, idx: number) => {
        const blogPageData = scrollItem?.elements;
        const blogData = blogPageData?.other_metadata?.value[0];

        if (!blogData) {
            return null;
        }

        const postImage = getImageData(blogData.elements?.blog_scroll_image);

        const minutesToRead = getTimeToRead(scrollItem);

        return (
            <div key={idx} className={styles.item}>
                {minutesToRead > 0 && (
                    <p className={styles.readTime}>{minutesToRead} min read</p>
                )}
                <h3 data-kontent-element-codename="title">
                    {blogData?.elements?.title?.value}
                </h3>
                {postImage?.image && (
                    <LazyImage
                        src={optimizeKontentImageUrl(postImage.image.url, {
                            w: 430,
                            h: 250,
                            fit: 'crop',
                            fm: 'webp',
                            q: 80,
                        })}
                        srcSet={buildKontentSrcSet(postImage.image.url, 430, {
                            fm: 'webp',
                            q: 80,
                            h: 250,
                            fit: 'crop',
                        })}
                        sizes="(max-width: 576px) 100vw, 430px"
                        width={430}
                        height={250}
                        alt={postImage.alt}
                        data-kontent-element-codename="blog_scroll_image"
                        className={styles.itemImage}
                    />
                )}
                <p
                    className={styles.description}
                    data-kontent-element-codename="short_description"
                >
                    {blogData?.elements?.short_description?.value}
                </p>
                <p>
                    <a
                        href={`${langPrefix}${blogPageData.slug.url}`}
                        className={styles.cta}
                    >
                        {cta_label?.value || 'Read More'}
                    </a>
                </p>
            </div>
        );
    };

    const getPublishDate = (scrollItem: BlogPagePreview) => {
        const mastheadComponent =
            scrollItem?.elements?.page_components?.value?.find(
                (component: any) => component.system?.type === 'blog_masthead'
            );
        return mastheadComponent?.elements?.display_publish_date?.value || null;
    };

    const renderFeaturedBlog = (scrollItem: BlogPagePreview) => {
        const blogPageData = scrollItem?.elements;
        const blogData = blogPageData?.other_metadata?.value[0];

        if (!blogData) {
            return null;
        }

        const postImage = getImageData(blogData.elements?.blog_scroll_image);

        const minutesToRead = getTimeToRead(scrollItem);
        const publishDate = getPublishDate(scrollItem);

        return (
            <div className={styles.featuredBlog}>
                {postImage?.image && (
                    <LazyImage
                        src={optimizeKontentImageUrl(postImage.image.url, {
                            w: 430,
                            h: 250,
                            fit: 'crop',
                            fm: 'webp',
                            q: 80,
                        })}
                        srcSet={buildKontentSrcSet(postImage.image.url, 430, {
                            fm: 'webp',
                            q: 80,
                            h: 250,
                            fit: 'crop',
                        })}
                        sizes="(max-width: 576px) 100vw, 430px"
                        width={430}
                        height={250}
                        alt={postImage.alt}
                        data-kontent-element-codename="blog_scroll_image"
                        className={styles.itemImage}
                    />
                )}
                <div className={styles.featuredBlogContent}>
                    {minutesToRead > 0 && (
                        <div className={styles.readTime}>
                            {minutesToRead} min read
                        </div>
                    )}
                    <h3 data-kontent-element-codename="title">
                        {blogData?.elements?.title?.value}
                    </h3>
                    <p
                        className={styles.description}
                        data-kontent-element-codename="short_description"
                    >
                        {blogData?.elements?.short_description?.value}
                    </p>
                    <div className={styles.ctaButtonWrapper}>
                        <CtaLink
                            href={`${langPrefix}${blogPageData.slug.url}`}
                            elementCodename="cta_label"
                        >
                            {cta_label?.value || 'Learn More'}
                        </CtaLink>
                    </div>
                    {publishDate && (
                        <div className={styles.metaInfo}>
                            <span className={styles.publishDate}>
                                {publishDate}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderListItem = (
        scrollItem: BlogPagePreview,
        idx: number,
        showImage: boolean = false
    ) => {
        const blogPageData = scrollItem?.elements;
        const blogData = blogPageData?.other_metadata?.value[0];

        if (!blogData) {
            return null;
        }
        const postImage = showImage
            ? getImageData(blogData.elements?.blog_scroll_image)
            : null;
        const minutesToRead = getTimeToRead(scrollItem);
        const publishDate = getPublishDate(scrollItem);

        return (
            <div key={idx} className={styles.listItem}>
                {showImage && postImage?.image && (
                    <div className={styles.listItemImage}>
                        <LazyImage
                            src={optimizeKontentImageUrl(postImage.image.url, {
                                w: 180,
                                h: 180,
                                fit: 'crop',
                                fm: 'webp',
                                q: 80,
                            })}
                            srcSet={buildKontentSrcSet(
                                postImage.image.url,
                                180,
                                {
                                    fm: 'webp',
                                    q: 80,
                                    h: 180,
                                    fit: 'crop',
                                }
                            )}
                            sizes="180px"
                            width={180}
                            height={180}
                            alt={postImage.alt}
                            data-kontent-element-codename="blog_scroll_image"
                            className={styles.itemImage}
                        />
                    </div>
                )}
                <div className={styles.listItemContent}>
                    {minutesToRead > 0 && (
                        <div className={styles.readTime}>
                            {minutesToRead} min read
                        </div>
                    )}
                    <h3 data-kontent-element-codename="title">
                        {blogData?.elements?.title?.value}
                    </h3>
                    <div className={styles.ctaButtonWrapper}>
                        <CtaLink
                            href={`${langPrefix}${blogPageData.slug.url}`}
                            elementCodename="cta_label"
                        >
                            {cta_label?.value || 'Learn More'}
                        </CtaLink>
                    </div>
                    {publishDate && (
                        <div className={styles.metaInfo}>
                            <span className={styles.publishDate}>
                                {publishDate}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Gallery layout: Latest blog on left, scrollable list on right (desktop)
    // On mobile: All scrollable, latest blog with photo
    if (isGalleryLayout) {
        const featuredBlog = content[0];
        const listBlogs = content.slice(1);

        // On mobile, render all blogs in a single scrollable list
        if (!isLargeScreen) {
            return (
                <section
                    id={anchor_name?.value}
                    className={styles.wrapper}
                    data-kontent-item-id={kontentItemId}
                >
                    {headline && (
                        <h2 data-kontent-element-codename="headline">
                            {headline?.value}
                        </h2>
                    )}

                    <div className={styles.version2Layout}>
                        <div className={styles.scrollableList}>
                            {featuredBlog &&
                                renderListItem(featuredBlog, 0, true)}
                            {listBlogs.map((item, idx) =>
                                renderListItem(item, idx + 1, true)
                            )}
                        </div>
                    </div>
                </section>
            );
        }

        // Desktop: Featured blog on left, scrollable list on right
        return (
            <section
                id={anchor_name?.value}
                className={styles.wrapper}
                data-kontent-item-id={kontentItemId}
            >
                {headline && (
                    <h2 data-kontent-element-codename="headline">
                        {headline?.value}
                    </h2>
                )}

                <div className={styles.version2Layout}>
                    {featuredBlog && (
                        <div className={styles.featuredContainer}>
                            {renderFeaturedBlog(featuredBlog)}
                        </div>
                    )}
                    {listBlogs.length > 0 && (
                        <div className={styles.scrollableList}>
                            {listBlogs.map((item, idx) =>
                                renderListItem(item, idx, true)
                            )}
                        </div>
                    )}
                </div>
            </section>
        );
    }

    // Version 1 layout: Existing carousel/stacked layout
    return (
        <section
            id={anchor_name?.value}
            className={styles.wrapper}
            data-kontent-item-id={kontentItemId}
        >
            {headline && (
                <h2 data-kontent-element-codename="headline">
                    {headline?.value}
                </h2>
            )}

            <div
                className={
                    layoutDisplayType === 'carousel'
                        ? styles.carousel
                        : styles.stacked
                }
            >
                {layoutDisplayType === 'carousel' && currentItem > 0 ? (
                    <ArrowLgButton
                        direction="left"
                        strokeColor="#f06623"
                        handleClick={handleClickPrev}
                        className={styles.prev}
                    />
                ) : null}
                <div className={styles.items}>
                    {layoutDisplayType === 'carousel'
                        ? visibleItems?.map((item, idx) =>
                              renderItem(item, idx)
                          )
                        : content?.map((item, idx) => renderItem(item, idx))}
                </div>
                {layoutDisplayType === 'carousel' &&
                currentItem < content?.length - 1 ? (
                    <ArrowLgButton
                        direction="right"
                        strokeColor="#f06623"
                        handleClick={handleClickNext}
                        className={styles.next}
                    />
                ) : null}
            </div>
        </section>
    );
};

export default BlogScroll;
